import { CouponDiscountTypeEnum } from '@/service/api/couponPage'
import classNames from 'classnames'
import type { ReactNode } from 'react'

/** 设计稿主色 */
const ACCENT = '#5751FF'
const BG = '#F5F6FF'

export interface CouponVisualBlockProps {
  /** 1 折扣券 2 满减券 */
  discountType: string
  /** 折扣为百分比数字；满减为减免金额（日元） */
  discountValue: number
  /** 如 JPY2,000可用 */
  minSpendLabel: string
  /** 券标题 */
  couponTitle: string
  /** 短到期文案 */
  shortExpiry: string
  /**
   * 右侧附加区（印章、操作按钮等），由外部传入。
   * 不传则不渲染第二列，主文案区占满右侧。
   */
  rightExtra?: ReactNode
  /**
   * 「使用说明」等区域，由外部传入（可传按钮等交互节点），展示在券内标题/到期日下方。
   * 不传则不占高度，券体保持固定高度。
   */
  usageHintSlot?: ReactNode
  className?: string
}

/**
 * 优惠券中部主视觉：浅紫底、左右分区、虚线分割 + 上下半圆缺口；
 * 右侧为「主文案 flex-1 + 可选 rightExtra」横向排列。
 */
const CouponVisualBlock = ({
  discountType,
  discountValue,
  minSpendLabel,
  couponTitle,
  shortExpiry,
  rightExtra,
  usageHintSlot,
  className,
}: CouponVisualBlockProps) => {
  const isPercent = discountType === CouponDiscountTypeEnum.DISCOUNT
  const hasExtra = rightExtra != null && rightExtra !== false
  const hasUsageHint =
    usageHintSlot != null && usageHintSlot !== false

  return (
    <div className={classNames('relative', className)}>
      <div
        className={classNames(
          'relative flex overflow-hidden rounded-[12px]',
          hasUsageHint ? 'min-h-[108px]' : 'h-[92px]'
        )}
        style={{ backgroundColor: BG }}
      >
        {/* 左侧：折扣信息 */}
        <div className='flex w-[96px] min-w-[92px] shrink-0 flex-col items-center justify-center gap-1.5 px-2 py-4'>
          {isPercent ? (
            <div className='flex flex-wrap items-baseline justify-center gap-0.5'>
              <span
                className='text-[22px] font-bold leading-none tracking-tight'
                style={{ color: ACCENT }}
              >
                {Math.min(100, Math.round(discountValue))}%
              </span>
              <span
                className='text-[14px] font-bold leading-none uppercase'
                style={{ color: ACCENT }}
              >
                OFF
              </span>
            </div>
          ) : (
            <div className='flex flex-wrap items-baseline justify-center gap-0.5'>
              <span
                className='text-[14px] font-bold leading-none uppercase'
                style={{ color: ACCENT }}
              >
                JPY
              </span>
              <span
                className='text-[20px] font-bold leading-none'
                style={{ color: ACCENT }}
              >
                {discountValue}
              </span>
            </div>
          )}
          <div
            className='max-w-full text-center text-[12px] font-medium leading-snug'
            style={{ color: ACCENT }}
          >
            {minSpendLabel}
          </div>
        </div>

        {/* 中间：虚线 + 上下半圆缺口 */}
        <div className='relative z-[1] flex w-0 shrink-0 justify-center self-stretch'>
          <div
            className='pointer-events-none absolute left-1/2 top-[0px] bottom-[0px] w-0 -translate-x-1/2 border-l border-dashed'
            style={{ borderColor: `${ACCENT}40` }}
          />
          <div
            className='pointer-events-none absolute left-1/2 top-0 z-[2] size-[14px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-white shadow-[0_0_0_1px_rgba(92,92,230,0.06)]'
            aria-hidden
          />
          <div
            className='pointer-events-none absolute left-1/2 bottom-0 z-[2] size-[14px] -translate-x-1/2 translate-y-1/2 rounded-full bg-white shadow-[0_0_0_1px_rgba(92,92,230,0.06)]'
            aria-hidden
          />
        </div>

        {/* 右侧：主信息 flex-1 + 可选附加区 */}
        <div
          className={classNames(
            'flex flex-1 items-stretch min-w-0',
            hasExtra ? 'gap-1 pr-2' : 'pr-3'
          )}
        >
          <div
            className={classNames(
              'flex min-h-0 min-w-0 flex-1 flex-col justify-center py-3 pl-4',
              hasUsageHint ? 'py-2.5' : 'py-3.5',
              !hasExtra && 'pr-3'
            )}
          >
            <div className='text-[14px] font-bold leading-snug text-[#333333] line-clamp-2'>
              {couponTitle}
            </div>
            <div className='mt-1.5 text-[12px] leading-relaxed text-[#999999]'>
              {shortExpiry}
            </div>
            {hasUsageHint ? (
              <div className='mt-1.5 min-w-0 self-start'>{usageHintSlot}</div>
            ) : null}
          </div>
          {hasExtra ? (
            <div className='flex shrink-0 items-center justify-center self-stretch py-2 pl-1'>
              {rightExtra}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  )
}

export default CouponVisualBlock
